import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import DropletOptions from '../_bubble-options';
import NavOptions from './components/_edit-options';
import NavLink from './components/nav-link';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('navigation', {
  template: require('./template.html'),
  mixins: [cssGenerator],
  computed: {
    ...mapGetters([
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'bouncePluginHook',
      'browser',
      'dragging',
      'globalBubbleOptionsOpen',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    routeWatch() {
      const r = this.$store.getters.route;
      if (r === 'page-editor') {
        this.unBindPlugin();
        this.bindPlugin();
      }
      return r;
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen;
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    searchOptions() {
      return JSON.stringify({
        file: `${this._servePrefix('')}/_siteSearch/site-search.json`,
      });
    },
    fixedHelperClass() {
      const underDesktop = this.viewport !== 'desktop';
      let helper = '';
      if (underDesktop && this.droplet.scrollType === 'fixed') {
        // add class
        helper = `editorUi-${this.viewport}Nav-fixed`;
      }
      if (underDesktop && this.droplet.scrollType === 'sticky') {
        // add class
        helper = `editorUi-${this.viewport}Nav-sticky`;
      }
      return helper;
    },
    opacity() {
      const selector = _.find(this.droplet.mediaQueries, { selector: '.drzNav' });
      const property = _.find(selector.properties, { property: 'background-color' });
      let o = 1;
      const value = property[this.viewport];
      if (value !== 'default') {
        o = this._buildPickerVal(property[this.viewport]).a;
      }
      if (this.viewport !== 'desktop' && value === 'default' && property.desktop !== 'default') {
        o = this._buildPickerVal(property.desktop).a;
      }
      return o;
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzNav' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        perspective: null,
        logoSize: null,
        innerWidth: null,
        barBorderTop: null,
        barBorderBottom: null,
        barBorderLeft: null,
        barBorderRight: null,
        barRadiusTopLeft: null,
        barRadiusTopRight: null,
        barRadiusBottomLeft: null,
        barRadiusBottomRight: null,
        barLinkSize: null,
        tlPaddingTop: null,
        tlPaddingBottom: null,
        tlPaddingLeft: null,
        tlPaddingRight: null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'border-top-left-radius': null,
        'border-top-right-radius': null,
        'border-bottom-left-radius': null,
        'border-bottom-right-radius': null,
        'padding-top': null,
        'padding-bottom': null,
        'padding-right': null,
        'padding-left': null,
        'letter-spacing': null,
        'box-shadow': null,
      },
      fixedDeb: null,
      styleSidebarWidth: 350,
      hidden: false,
      navOverlay: null,
      fixedRules: {},
      fixedNavHelpers: {},
      helperClass: null,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-wide',
      showEditBubble: false,
      wait: null,
      showRevealClass: false,
    };
  },
  name: 'navigation',
  props: [
    'block-index',
    'droplet',
    'items',
    'index',
    'canvas',
  ],
  components: {
    DropletOptions,
    NavOptions,
    NavLink,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
      // need to reset plugin in case nav is sticky/fixed
      setTimeout(() => {
        this.unBindPlugin();
        this.bindPlugin();
      }, 500);
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzNav',
        });
      }
    },
    viewport() {
      const nav = this.$refs.drzNav;
      const wasStuck = nav &&
        nav.classList.contains('drzNav-sticky-set') &&
        this.viewport !== 'desktop';
      this.callCssGenerator();
      this.observeVisibility();
      // we need to account for the animation change when viewports
      // switch, then we can bounce the plugin
      setTimeout(() => {
        this.unBindPlugin();
        this.bindPlugin();
        // when a nav is sticky and a non desktop viewport is switched, we need to
        // readd the sticky class as it dissappears in this instance
        if (wasStuck) {
          nav.classList.add('drzNav-sticky-set');
        }
      }, 350);
    },
    showEditBubble(open) {
      if (open) {
        this.fixBubble();
      }
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
    bouncePluginHook(id) {
      if (this.droplet._id === id) {
        setTimeout(() => {
          this.unBindPlugin();
          this.bindPlugin();
          this.$store.dispatch('callPluginBounce', false);
        }, 250);
      }
    },
  },
  created() {
    this.checkNewValues();
  },
  mounted() {
    this.callCssGenerator();
    $(document).ready(async () => {
      this.navOverlay = this.$refs.drpOptions.$refs.drpOverlay;
      await this.bindPlugin();
      this.checkFixed();
    });
    this.observeVisibility();
  },
  beforeDestroy() {
    this.unBindPlugin();
  },
  destroyed() {
    // runs after removing nav from page
    if ($.fn.drzNav.setScrolling) {
      $.fn.drzNav.setScrolling();
    }
    window.removeEventListener('scroll', this.fixBubble, false);
    $(window).off('resize', this.fixBubble);
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    checkNewValues() {
      const values = [];
      if (!this.droplet.siteSearch) {
        values.push({ key: 'siteSearch', value: false });
      }
      if (values.length > 0) {
        this.$store.dispatch('setNewDropletValues', {
          drp: this.droplet,
          vm: this,
          values,
        });
      }
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    fixBubble() {
      const nav = this.$refs.drzNav;
      this._debounce(() => {
        const $navRow = this.$el.querySelectorAll(`#drp-${this.droplet._id}`)[0];
        const fixed = $navRow.getAttribute('data-nav-fixed') === 'true';
        if (fixed) {
          const navStyles = window.getComputedStyle(nav, null);
          const navTop = navStyles.getPropertyValue('top');
          const navHeight = navStyles.getPropertyValue('height');
          this.fixedRules = {
            position: 'fixed',
            left: 0,
            top: navTop,
            height: navHeight,
            zIndex: 11,
          };
          this.helperClass = 'drz-fixed-inSection';
          this.navOverlay.style.zIndex = '8';
          // when a nav is fixed and a user changes the viewport, we need to adjust the
          // width of the nav to match the body container
          if (this.activeDroplet._id) {
            // cover desktop adjustments
            if (this.viewport === 'desktop') {
              const newNavWidth = window.outerWidth - this.styleSidebarWidth;
              this.fixedRules.width = `${newNavWidth}px`;
            } else {
              this.fixedRules.right = `${this.styleSidebarWidth}px`;
            }
          }
          // in the event a nav is fixed and there is a style bar open for another droplet, the nav
          // somehow gets it's top value messed up so we need a helper for that in some browsers.
          if (this.activeDroplet._id && this.activeDroplet._id !== this.droplet._id && this.browser.name !== 'safari') {
            this.fixedNavHelpers.marginTop = `-${navTop}`;
          } else {
            this.fixedNavHelpers = {};
          }
        } else {
          this.fixedRules = {};
          this.fixedNavHelpers = {};
          this.helperClass = null;
          this.navOverlay.style.zIndex = '';
        }
      }, 5);
      // this is in place to recheck the height and top values of the nav as there are some
      // transition values that can cause improper calculations
      clearTimeout(this.fixedDeb);
      this.fixedDeb = setTimeout(() => {
        const navStyles = window.getComputedStyle(nav, null);
        const navTop = navStyles.getPropertyValue('top');
        const navHeight = navStyles.getPropertyValue('height');
        this.fixedRules.top = navTop;
        this.fixedRules.height = navHeight;
      }, 500);
    },
    checkFixed() {
      // since navs can be fixed, we need to allow the nav wrapper, which also
      // servers as a wrapper for the edit bubble, to become fixed so the bubble
      // can remain under it. we use these listeners to check if fixed, then we
      // adjust the wrapper and add helpers as needed.
      const $el = this.$el;
      if ($el) {
        this.fixBubble();
        window.removeEventListener('scroll', this.fixBubble, false);
        window.addEventListener('scroll', this.fixBubble, false);
        $(window).on('resize', this.fixBubble);
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    debounce(fn, delay) {
      clearTimeout(this.wait);
      this.wait = setTimeout(() => {
        fn();
      }, delay);
    },
    changeBubbleSize(size) {
      this.setBubbleSize = `dropletBubble-${size}`;
    },
    bindPlugin() {
      return new Promise((resolve) => {
        const $nav = $(this.$refs.drzNav);
        const $searchModal = $nav.find('.drzNav-search-btn');
        const $searchInput = $nav.find('.drzModal-search-bar');
        // only if in editor, we pass in the opacity as a
        // parameter because of the css transition delay.
        $nav.drzNav({
          topBuffer: 65,
        });
        $nav.drzAnchorScroll();
        // init site searching
        $searchModal.drzModal();
        $searchInput.drzSiteSearch();
        resolve();
      });
    },
    unBindPlugin() {
      return new Promise((resolve) => {
        const $nav = $(this.$refs.drzNav);
        const $searchInput = $nav.find('.drzModal-search-bar');
        if ($nav.drzNav.destroy) {
          $nav.drzNav.destroy($nav);
          $nav.drzAnchorScroll.destroy($nav);
        }
        if ($searchInput.drzSiteSearch.destroy) {
          $searchInput.drzSiteSearch.destroy($searchInput);
        }
        resolve();
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/navigation/index.js